import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { getCurrentUserCompanyId } from '@/lib/userUtils';
import { useToast } from '@/hooks/use-toast';
import { CheckCircle2 } from 'lucide-react';
import { applyBankRulesToNewTransactions } from '@/lib/applyTrainedMappings';

interface BankAccountsPhase2Step5Props {
  onNext: () => void;
  onPrevious: () => void;
  accountId: string | null;
}

export const BankAccountsPhase2Step5 = ({ onNext, onPrevious, accountId }: BankAccountsPhase2Step5Props) => {
  const { toast } = useToast();
  const [syncEnabled, setSyncEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  const handleComplete = async () => {
    try {
      setSaving(true);
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) throw new Error('Company ID not found');

      // Get all connected accounts
      const { data: connectedAccounts, error: accountsError } = await supabase
        .from('bank_accounts')
        .select('id')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .eq('connection_status', 'connected')
        .not('teller_access_token', 'is', null)
        .not('teller_account_id', 'is', null);

      if (accountsError) throw accountsError;

      if (!connectedAccounts || connectedAccounts.length === 0) {
        toast({
          title: 'No connected accounts',
          description: 'Please connect at least one account to enable sync',
          variant: 'destructive',
        });
        return;
      }

      // Update sync settings for all connected accounts
      const accountIds = connectedAccounts.map(acc => acc.id);
      const { error: updateError } = await supabase
        .from('bank_accounts')
        .update({
          sync_enabled: syncEnabled,
          last_synced_at: new Date().toISOString(),
        })
        .in('id', accountIds);

      if (updateError) throw updateError;

      // Update training progress for all accounts
      const { error: progressError } = await supabase
        .from('bank_account_training_progress')
        .update({
          status: 'completed',
        })
        .eq('company_id', companyId)
        .in('account_id', accountIds);

      if (progressError) {
        console.error('Error updating training progress:', progressError);
      }

      // Apply trained rules to any remaining uncategorized transactions
      try {
        for (const accountId of accountIds) {
          await applyBankRulesToNewTransactions(companyId, accountId);
        }
      } catch (ruleError) {
        console.warn('Non-critical: Could not apply rules to transactions:', ruleError);
      }

      // Ensure all bank transactions have the source field set (for existing data migration)
      try {
        const { error: sourceUpdateError } = await supabase
          .from('bank_transactions')
          .update({ source: 'bank' })
          .eq('company_id', companyId)
          .in('account_id', accountIds)
          .is('source', null);

        if (sourceUpdateError) {
          console.warn('Non-critical: Could not update transaction sources:', sourceUpdateError);
        }
      } catch (sourceError) {
        console.warn('Non-critical: Error updating transaction sources:', sourceError);
      }

      toast({
        title: 'Success',
        description: `Bank account setup completed for ${accountIds.length} account${accountIds.length > 1 ? 's' : ''}. Transactions are now included in financial reports.`,
      });

      onNext();
    } catch (error: any) {
      console.error('Error completing setup:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to complete setup',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Confirm Ongoing Live Sync & Future Rules</h3>
        <p className="text-sm text-muted-foreground">
          Enable automatic daily sync and configure how future transactions will be handled.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Live Sync Settings</CardTitle>
          <CardDescription>
            Configure automatic transaction syncing
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="sync-enabled">Enable Automatic Daily Sync</Label>
              <p className="text-sm text-muted-foreground">
                New transactions will be automatically imported and categorized daily
              </p>
            </div>
            <Switch
              id="sync-enabled"
              checked={syncEnabled}
              onCheckedChange={setSyncEnabled}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span className="text-sm">Bank account connected</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span className="text-sm">Transactions imported</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span className="text-sm">Categorization rules trained</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span className="text-sm">YTD transactions categorized</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span className="text-sm">Live sync {syncEnabled ? 'enabled' : 'disabled'}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onPrevious}>
          Previous Step
        </Button>
        <Button onClick={handleComplete} disabled={saving}>
          {saving ? 'Completing...' : 'Complete Section'}
        </Button>
      </div>
    </div>
  );
};

